import CustomImage from "@lhd/components/CustomImage";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { Tooltip } from "antd";
import { Styles } from "./styled";
import {
  useFetchOneAppByTypeV2,
  useFetchRecordV2,
  useGetAuth,
} from "@lhd/hooks";
import { CONTRACT_STATUS, SignerStatus } from "@lhd/types";
import dayjs from "dayjs";
import { parseJson } from "@lhd/utility";
import ButtonSign from "../../components/ButtonSign";
import { useParams, useSearchParams } from "react-router-dom";
import { ROLE_SIGNATURE } from "../../constants";
import { useMemo } from "react";
import { CONTRACT_MESSAGES_CLONE_APP } from "@lhd/constants";

interface IProps {
  item: AppPartType;
  namePath?: string | string[];
  appType?: string;
  recordId?: string;
  appId?: string;
  isQuickViewMode?: boolean;
  isCreate?: boolean;
}

const Signature = (props: IProps) => {
  const {
    item,
    namePath,
    appType = "",
    recordId = "",
    appId = "",
    isQuickViewMode = false,
    isCreate,
  } = props;
  const isAppPartEmployee = item.field_code === "employee_signature";
  const { getCurrentWorker, isUserResigned } = useGetAuth();
  const currentWorker = getCurrentWorker();
  const {
    companyId: companyIdParam,
    role: roleParam = ROLE_SIGNATURE.EMPLOYER,
  } = useParams();

  const [searchParams] = useSearchParams();
  const pageMode = searchParams.get("pageMode");

  const {
    data: recordDataRaw,
    isLoading: isLoadingRecord,
    isSuccess: isSuccessRecord,
    refetch: refetchRecord,
  } = useFetchRecordV2({ appType, appId, recordId, companyId: companyIdParam });

  const { data: dataAppRaw } = useFetchOneAppByTypeV2(appType);

  const signatures = useMemo(() => {
    return recordDataRaw?.data.contract_info.signatures.find(
      (x) => x.part_id === Number(item.id),
    );
  }, [recordDataRaw?.data.contract_info.signatures, item.id]);

  const isCurrentWorkerSigned = useMemo(() => {
    const signature = signatures?.signers.find(
      (x) =>
        x.status === SignerStatus.SIGNED &&
        x.worker_id === Number(currentWorker?.id),
    );
    return !!signature;
  }, [signatures, currentWorker?.id]);

  const contractStatus = recordDataRaw?.data.contract_info.contract_status;
  let disabled = true;

  const appPartRelation = dataAppRaw?.app_parts.find(
    (x) => Number(x.id) === Number((item as any).app_part_relation_id),
  );
  const rawCurrentWorkerValues = recordDataRaw?.data.regular_values.find(
    (x) => x.app_part_id === Number(appPartRelation?.id),
  )?.value;
  const currentWorkerValues = Array.isArray(rawCurrentWorkerValues)
    ? rawCurrentWorkerValues
    : parseJson(rawCurrentWorkerValues, [] as any[]);

  if (
    currentWorkerValues?.includes(Number(currentWorker?.id)) &&
    contractStatus === CONTRACT_STATUS.PENDING_SIGNATURE &&
    !isCurrentWorkerSigned
  ) {
    disabled = false;
  }
  const renderSigned = (
    signedDateValue: string | null,
    signatureImageUrl: string | null,
  ) => {
    return (
      <div className="signed_info_grid">
        <span className="signed_info_grid__label">電子署名日</span>
        <span className="signed_info_grid__value">
          {dayjs(signedDateValue).format("YYYY年MM月DD日")}
        </span>
        {signatureImageUrl && (
          <>
            <span className="signed_info_grid__label">電子署名</span>
            <span className="signed_info_grid__value">
              <CustomImage
                src={signatureImageUrl}
                width={100}
                style={{ maxHeight: 100 }}
              />
            </span>
          </>
        )}
      </div>
    );
  };

  const isNotRightRole =
    (isAppPartEmployee && roleParam !== ROLE_SIGNATURE.EMPLOYEE) ||
    (!isAppPartEmployee && roleParam !== ROLE_SIGNATURE.EMPLOYER);

  const handleGetTooltipTitle = () => {
    if (isCreate) {
      return CONTRACT_MESSAGES_CLONE_APP.SIGNING_NOT_AVAILABLE_WHILE_CREATING;
    }
    if (isQuickViewMode) {
      return "";
    }
    if (isCurrentWorkerSigned) {
      return CONTRACT_MESSAGES_CLONE_APP.ALREADY_SIGNED_CAN_NOT_SIGN_AGAIN;
    }
    if (contractStatus === CONTRACT_STATUS.DRAFTING) {
      return CONTRACT_MESSAGES_CLONE_APP.SIGNING_NOT_AVAILABLE_WHILE_DRAFTING;
    }
    if (isNotRightRole && roleParam === ROLE_SIGNATURE.EMPLOYEE) {
      return CONTRACT_MESSAGES_CLONE_APP.COMPANY_SIDE_SIGN;
    }
    if (isNotRightRole && roleParam === ROLE_SIGNATURE.EMPLOYER) {
      return CONTRACT_MESSAGES_CLONE_APP.EMPLOYEE_SIDE_SIGN;
    }

    return CONTRACT_MESSAGES_CLONE_APP.ONLY_SIGNER_CAN_SIGN;
  };
  const renderBtnSign = () => {
    const isValidRole = Object.values(ROLE_SIGNATURE).includes(
      roleParam as ROLE_SIGNATURE,
    );

    if (!isValidRole) return null;

    return (
      <div style={{ display: "inline-flex" }}>
        <Tooltip
          title={handleGetTooltipTitle()}
          trigger="hover"
          open={
            (disabled || isNotRightRole) && !isQuickViewMode && !isUserResigned
              ? undefined
              : false
          }
          placement="topLeft"
        >
          <div>
            <ButtonSign
              role={roleParam as ROLE_SIGNATURE}
              appType={appType}
              appId={appId}
              recordId={recordId}
              disabled={
                disabled ||
                isNotRightRole ||
                isQuickViewMode ||
                isUserResigned ||
                pageMode === "view"
              }
              onClick={() => refetchRecord()}
            />
          </div>
        </Tooltip>
      </div>
    );
  };

  const signedSigners = signatures?.signers.filter(
    (signer) => signer.status === SignerStatus.SIGNED,
  );
  const pendingSigners = signatures?.signers.filter(
    (signer) => signer.status === SignerStatus.PENDING,
  );

  return (
    <Styles className="label_container">
      {signedSigners?.map((signer) => {
        return (
          signer.status === SignerStatus.SIGNED &&
          renderSigned(signer.signed_at, signer.signature_image_url)
        );
      })}
      {(pendingSigners?.length || !signatures?.signers?.length) &&
        renderBtnSign()}
    </Styles>
  );
};
export default Signature;
